/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * eventCertificates
 * -----------------
 * Step 4 of the event wizard (an event that is already saved): the
 * certificate template each activity uses, and ONE organization name and logo
 * for the whole event.
 *
 *   activity.certificate  { choiceId, templateId, locked, dirty }
 *       one row of event_activity_certificate_template per activity;
 *       `locked` = the activity already has issued certificates. It is a
 *       notice, not a lock: the template, name and logo can still change, and
 *       issued certificates show the change on their next download (they are
 *       built from these rows every time).
 *   form.certificate_org  { organizationId, name, logoUrl, savedName, savedLogoUrl, choiceIds, loaded }
 *       the event's organization (organization.name / logo_url), printed on
 *       every certificate of the event; `choiceIds` = every saved choice row of
 *       the event, all of which carry the name and logo
 *
 * Written in the wizard's own Save draft / Publish batch: the organization row,
 * and every choice row of the event. Deleting the logo is immediate instead
 * (see deleteLogoOperations).
 */

export const CERTIFICATE_CHOICE_TABLE = 'event_activity_certificate_template';
export const CERTIFICATE_TEMPLATE_TABLE = 'certificate_template';
export const ISSUED_CERTIFICATE_TABLE = 'user_certificate_information';
export const ORGANIZATION_TABLE = 'organization';
export const ORGANIZATION_NAME_MAX = 256;

/**
 * A deleted logo keeps its link with this prefix: the file is gone from
 * storage, but the column (required on the choice rows) is never empty. Every
 * reader treats a prefixed link as "no logo", and Save asks for a new one.
 */
export const DELETED_LOGO_PREFIX = 'deleted:';
export const isDeletedLogo = (url) => String(url || '').startsWith(DELETED_LOGO_PREFIX);
export const liveLogo = (url) => (url && !isDeletedLogo(url) ? String(url) : '');

export function emptyCertificate() {
  return { choiceId: null, templateId: '', locked: false, dirty: false };
}

export const certificateOf = (activity) => activity?.certificate || emptyCertificate();

export const isSavedEvent = (form) => form?.id !== undefined && form?.id !== null && form?.id !== '';

/**
 * The event's organization details, from its organization row and saved
 * choices. When a saved choice row carries a different name or logo (rows
 * written before the organization became one per event), the details count as
 * changed, so the next Save brings every row in line.
 */
export function orgFromRows(organization, choiceRows) {
  const name = String(organization?.name || '');
  const logo = String(organization?.logo_url || '');
  const outOfStep = (choiceRows || []).some((row) => String(row.organization_name || '') !== name
    || String(row.organization_logo_url || '') !== logo);
  return {
    organizationId: organization?.id ?? null,
    name,
    logoUrl: logo,
    savedName: outOfStep ? null : name,
    savedLogoUrl: logo,
    choiceIds: (choiceRows || []).map((row) => row.id).filter((id) => id != null),
    loaded: true,
  };
}

export function orgChanged(org) {
  return Boolean(org?.loaded) && (org.name !== org.savedName || org.logoUrl !== org.savedLogoUrl);
}

/**
 * The saved choices matched onto saved activities: [{ index, certificate }]
 * for each activity that has a choice or issued certificates, and does not
 * hold a choice in the form already (a re-load never overwrites edits).
 * `issued` is { activityId: count } — a non-zero count marks the activity
 * `locked` (it has issued certificates; shown as a notice).
 */
export function hydrateCertificates(activities, choiceRows, issued = {}, idOf = (v) => v) {
  const byActivity = new Map((choiceRows || []).map((row) => [String(idOf(row.event_activity_id)), row]));
  const patches = [];
  (activities || []).forEach((activity, index) => {
    if (activity?.id == null || activity.id === '' || activity.certificate) return;
    const row = byActivity.get(String(activity.id));
    const locked = Number(issued[String(activity.id)]) > 0;
    if (!row && !locked) return;
    const templateId = row ? idOf(row.certificate_template_id) : null;
    patches.push({
      index,
      certificate: {
        choiceId: row ? row.id : null,
        templateId: templateId == null ? '' : String(templateId),
        locked,
        dirty: false,
      },
    });
  });
  return patches;
}

/** A changed template choice, marked dirty — also on an activity with issued certificates. */
export function editCertificate(certificate, patch) {
  return { ...certificate, ...patch, dirty: true };
}

/** 'issued' | 'saved' | 'ready' | 'none' — the activity card's chip. */
export function certificateStatus(certificate) {
  if (!certificate) return 'none';
  if (certificate.templateId && certificate.dirty) return 'ready';
  if (certificate.locked) return 'issued';
  if (certificate.templateId && certificate.choiceId != null) return 'saved';
  if (certificate.templateId) return 'ready';
  return 'none';
}

/** True when the event has certificates set up, or is about to. */
export function usesCertificates(form) {
  return (form?.activities || []).some((activity) => certificateOf(activity).templateId)
    || (form?.certificate_org?.choiceIds || []).length > 0;
}

/**
 * Errors for the wizard (keys belong to step 4). The organization name and a
 * live logo are needed once any activity has a template — every certificate
 * prints them. An untouched event with no certificates is not held up.
 */
export function certificateFormErrors(form) {
  const errors = {};
  const org = form?.certificate_org;
  if (!isSavedEvent(form) || !org?.loaded || !usesCertificates(form)) return errors;
  const name = String(org.name || '').trim();
  if (!name) errors.certificate_org_name = 'Enter the organization name printed on the certificates.';
  else if (name.length > ORGANIZATION_NAME_MAX) errors.certificate_org_name = `At most ${ORGANIZATION_NAME_MAX} characters.`;
  if (!liveLogo(org.logoUrl)) {
    errors.certificate_org_logo = isDeletedLogo(org.logoUrl)
      ? 'The logo was deleted. Upload a new logo before saving.'
      : 'Upload the organization logo printed on the certificates.';
  }
  return errors;
}

/**
 * What Save draft / Publish writes for step 4 (activities with issued
 * certificates included — their certificates follow on the next download):
 *   organization  the organization row, when its name or logo changed
 *   choices       event_activity_certificate_template rows:
 *     - an activity with a new or changed template → insert (no choice yet) or update;
 *     - when the name or logo changed, EVERY saved choice row of the event, so
 *       all of its certificates print the same organization.
 * New choices are only possible on a saved event (step 4 is closed before the
 * first save), so activities are named by id; a new activity in the same save
 * is named by its @ref through `activityKey`.
 */
export function certificateOperations(form, eventKey, activityKey) {
  const org = form?.certificate_org;
  if (!isSavedEvent(form) || !org?.loaded) return { organization: null, choices: [] };
  const name = String(org.name || '').trim();
  const logo = String(org.logoUrl || '').trim();
  const changed = orgChanged(org);
  const organization = changed && org.organizationId != null
    ? { table: ORGANIZATION_TABLE, data: { id: org.organizationId, name, logo_url: logo || null } }
    : null;

  const written = new Set();
  const choices = [];
  (form.activities || []).forEach((activity, index) => {
    const certificate = certificateOf(activity);
    if (!certificate.templateId) return;
    const templateChanged = certificate.dirty;
    if (!templateChanged && !(changed && certificate.choiceId != null)) return;
    // The template always goes along (unchanged when only the organization
    // changed), so the rows of one save share their columns and travel as one list.
    const row = { certificate_template_id: Number(certificate.templateId), organization_name: name, organization_logo_url: logo };
    if (certificate.choiceId != null) {
      row.id = certificate.choiceId;
      written.add(certificate.choiceId);
    } else {
      row.event_id = eventKey;
      row.event_activity_id = activityKey(activity, index);
    }
    choices.push(row);
  });
  // Saved choices whose activity is not in the form (or has no template now)
  // still carry the organization: keep them in step.
  if (changed) {
    (org.choiceIds || []).filter((id) => !written.has(id)).forEach((id) => {
      choices.push({ id, organization_name: name, organization_logo_url: logo });
    });
  }
  return { organization, choices };
}

/**
 * Deleting the logo, written at once: the organization row and every saved
 * choice row of the event keep the old link behind DELETED_LOGO_PREFIX.
 */
export function deleteLogoOperations(org) {
  const marked = `${DELETED_LOGO_PREFIX}${org.savedLogoUrl}`;
  const operations = [];
  if (org.organizationId != null) operations.push({ table: ORGANIZATION_TABLE, data: { id: org.organizationId, logo_url: marked } });
  if ((org.choiceIds || []).length) {
    operations.push({ table: CERTIFICATE_CHOICE_TABLE, data: org.choiceIds.map((id) => ({ id, organization_logo_url: marked })) });
  }
  return { operations, marked };
}

/**
 * Step 4's writes as batch operations: the organization row, then the choice
 * rows grouped by their columns (batch_save takes a list of rows only when
 * they all carry the same columns, and inserts apart from updates). Used by
 * the wizard's Save draft / Publish, and by step 4's own Save on a finished
 * event, where the event itself is not saved.
 */
export function certificateBatchOperations(form, eventKey, activityKey) {
  const { organization, choices } = certificateOperations(form, eventKey, activityKey);
  const groups = new Map();
  choices.forEach((row) => {
    const shape = `${row.id != null ? 'update' : 'insert'}:${Object.keys(row).filter((key) => key !== 'id').sort().join(',')}`;
    groups.set(shape, [...(groups.get(shape) || []), row]);
  });
  return [
    ...(organization ? [organization] : []),
    ...[...groups.values()].map((rows) => ({ table: CERTIFICATE_CHOICE_TABLE, data: rows })),
  ];
}

/**
 * Whether step 4 can be edited for this event. A finished (COMPLETED) event
 * is locked everywhere else in the wizard, but certificates are set up after
 * an event finishes, so step 4 stays open — with its own Save, which writes
 * only the certificate settings. A CANCELLED event stays read-only.
 */
export function certificatesEditable(form) {
  return String(form?.loaded_status || '').toUpperCase() !== 'CANCELLED';
}
